import { useState, useEffect } from 'react';
import { Row, Col, Card, Spinner, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faServer, 
  faClock, 
  faUsers, 
  faExclamationTriangle,
  faArrowUp,
  faTimes,
  faPaperPlane,
  faBan,
  faTimesCircle
} from "@fortawesome/free-solid-svg-icons";

import { iotmetrics } from '../../../service/telemetricDashboardService';
import { formatNumber } from '../utils/dataFormatter';

// Metrics configuration
const metricsConfig = [
  {
    key: 'AverageLatency',
    label: 'Avg Latency',
    icon: faClock,
    iconColor: 'text-warning',
    formatter: (value) => `${parseFloat(value).toFixed(2)}s`
  },
  {
    key: 'DeviceCount',
    label: 'Connected',
    icon: faUsers,
    iconColor: 'text-success',
    formatter: formatNumber
  },
  {
    key: 'D2CMessagesReceived',
    label: 'D2C Received',
    icon: faArrowUp,
    iconColor: 'text-info',
    formatter: formatNumber
  },
  {
    key: 'D2CMessagesDropped',
    label: 'D2C Dropped',
    icon: faTimes,
    iconColor: 'text-danger',
    formatter: (value) => value
  },
  {
    key: 'C2DMessagesExpired',
    label: 'C2D Expired',
    icon: faExclamationTriangle,
    iconColor: 'text-warning',
    formatter: (value) => value
  },
  {
    key: 'C2DMessagesSent',
    label: 'C2D Sent',
    icon: faPaperPlane,
    iconColor: 'text-primary',
    formatter: formatNumber
  },
  {
    key: 'C2DMessagesAbandoned',
    label: 'C2D Abandoned',
    icon: faBan,
    iconColor: 'text-secondary',
    formatter: (value) => value
  },
  {
    key: 'C2DMessagesRejected',
    label: 'C2D Rejected',
    icon: faTimesCircle,
    iconColor: 'text-danger',
    formatter: (value) => value
  }
];

const MetricsCards = ({ selectedManufacturer = 'toyota' }) => {
  const [iotData24H, setIotData24H] = useState([]);
  const [iotData7D, setIotData7D] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24H'); // '24H' or '7D'

  // Filter IOT Hub data based on selected manufacturer and time range
  const getFilteredIotData = () => {
    const currentData = timeRange === '24H' ? iotData24H : iotData7D;
    if (!currentData || currentData.length === 0) return [];
    
    return currentData.filter(item => {
      const hubName = item.rowKey.toUpperCase();
      switch (selectedManufacturer) {
        case 'toyota':
          return hubName === 'IOTHUB_TOYOTA';
        case 'raymond-us':
          return hubName === 'IOTHUB_RAYMOND';
        case 'raymond-eu':
          return hubName === 'IOTHUB_RAYMOND_EU';
        default:
          return hubName === 'IOTHUB_TOYOTA';
      }
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch both 24H and 7D data in parallel
        const [iot24HResponse, iot7DResponse] = await Promise.all([
          iotmetrics('24H'),
          iotmetrics('7D'),
        ]);
        
        setIotData24H(iot24HResponse?.data || []);
        setIotData7D(iot7DResponse?.data || []);
      } catch (err) {
        setError('Failed to fetch IOT Hub metrics');
        console.error('Error fetching IOT Hub data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []); // Only run once on component mount

  if (loading) {
    return (
      <Row className="mb-4">
        <Col className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading detailed IOT Hub metrics...</p>
        </Col>
      </Row>
    );
  }

  if (error) {
    return (
      <Row className="mb-4">
        <Col>
          <Card className="text-center border-danger">
            <Card.Body>
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-danger mb-2" size="2x" />
              <p className="text-danger">{error}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  const filteredData = getFilteredIotData();

  if (!filteredData || filteredData.length === 0) {
    return (
      <Row className="mb-4">
        <Col>
          <Card className="text-center">
            <Card.Body>
              <p>No IOT Hub data available for the selected company</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {filteredData.map((hub, index) => (
        <Row key={index} className="mb-4">
          <Col>
            <Card>
              <Card.Header className="bg-light">
                <div className="d-flex align-items-center justify-content-between">
                  <h6 className="mb-0">
                    <FontAwesomeIcon icon={faServer} className="me-2 text-primary" />
                    IOT Hub
                  </h6>
                  <div className="d-flex align-items-center">
                    <ButtonGroup size="sm">
                      <ToggleButton
                        id={`24H-${index}`}
                        type="radio"
                        variant="outline-primary"
                        name={`timeRange-${index}`}
                        value="24H"
                        checked={timeRange === '24H'}
                        onChange={(e) => setTimeRange(e.currentTarget.value)}
                        style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem'}}
                      >
                        24 Hrs
                      </ToggleButton>
                      <ToggleButton
                        id={`7D-${index}`}
                        type="radio"
                        variant="outline-primary"
                        name={`timeRange-${index}`}
                        value="7D"
                        checked={timeRange === '7D'}
                        onChange={(e) => setTimeRange(e.currentTarget.value)}
                        style={{fontSize: '0.75rem', padding: '0.25rem 0.5rem'}}
                      >
                        7 Days
                      </ToggleButton>
                    </ButtonGroup>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-wrap gap-1">
                  {metricsConfig.map((metric) => (
                    <div 
                      key={metric.key} 
                      className="flex-fill text-center bg-light border rounded p-2" 
                      style={{minWidth: '120px'}}
                    >
                      <FontAwesomeIcon 
                        icon={metric.icon} 
                        className={`${metric.iconColor} mb-1`} 
                        size="sm" 
                      />
                      <div style={{fontSize: '0.65rem'}} className="text-muted">
                        {metric.label}
                      </div>
                      <strong style={{fontSize: '0.75rem'}}>
                        {metric.formatter(hub[metric.key])}
                      </strong>
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ))}
    </>
  );
};

export default MetricsCards;
